'use client';
import { InputType } from '@rezi/types';
import { Icon, type IconProps } from '../icon';
import { Loading } from '../loading';
import { CopyToClipboard } from '../copy-to-clipboard';

import React, { useState } from 'react';

export type PrefilledField = {
  text: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  type?:
    | InputType['type']
    | React.InputHTMLAttributes<HTMLInputElement>['type'];
  fullWidth?: boolean;
  label?: string;
  value?: string | number;
  className?: string;
  loading?: boolean;
  inputSize?: 'small' | 'medium' | 'large';
  leadingIcon?: string;
  trailingIcon?: string;
  trailingIconDetail?: IconProps;
  helpText?: string;
  hidden?: boolean;
  validation?: boolean;
  validationMessage?: string;
  prefilledField?: PrefilledField;
  onClick?: (e: any) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  enterprise?: boolean;
  id?: string;
  name?: string;
  inputPrefix?: React.ReactNode;
  inputSuffix?: React.ReactNode;
  noEllipsis?: boolean;
  minLength?: number;
  maxLength?: number;
  disabledTextContrast?: boolean;
  enableCopyToClipboard?: boolean;
  customInputClass?: string;
  'data-test-id'?: string;
};

export const Input = ({
  ref,
  autoComplete = 'off',
  autoFocus = false,
  disabled = false,
  disabledTextContrast = false,
  type = 'text',
  name = '',
  placeholder = 'Enter here...',
  value,
  className,
  validation,
  validationMessage,
  leadingIcon: leading,
  trailingIcon = '',
  trailingIconDetail,
  inputSize = 'large',
  onFocus,
  onChange,
  onClick,
  loading,
  onKeyDown,
  onBlur,
  hidden = false,
  accept = '',
  readOnly = false,
  prefilledField,
  required = false,
  onMouseDown,
  enterprise = false,
  helpText,
  noEllipsis,
  id,
  inputPrefix,
  inputSuffix,
  minLength,
  maxLength,
  enableCopyToClipboard,
  customInputClass,
  'data-test-id': testId,
}: InputProps & {
  ref?: React.RefObject<HTMLInputElement | null>;
}) => {
  const [inputFocus, setInputFocus] = useState(false);
  const sizeClass = {
    small: `${enterprise ? 'leading-5 ' : ''} h-8 text-sm`,
    medium: 'h-10 text-base',
    large: 'h-10 text-base sm:h-12',
  };

  const handleOnFocus = (e: any) => {
    setInputFocus(true);
    if (onFocus) onFocus(e);
  };

  let gap = 'gap-1';
  const isDisabledStyle = disabled || loading;

  let inputClass = `${
    isDisabledStyle
      ? `bg-input-bg-disabled  ${disabledTextContrast ? 'text-gray-900' : 'text-input-disabled'}`
      : 'bg-input-bg text-gray-900'
  } h-6 w-full border-0! px-1 text-base ${enterprise ? 'font-normal' : 'font-semibold'} leading-6 placeholder:text-gray-300 ${noEllipsis ? ' ' : 'text-ellipsis'} focus:outline-hidden focus:ring-0 ${customInputClass}`;
  if (prefilledField) {
    inputClass += ' pl-0';
    gap = '';
  }

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (readOnly && value && type === 'text' && typeof value === 'string') {
      if (onClick) onClick(value);
    } else {
      if (onClick) onClick(e);
    }
  };

  const trailingIconClick = () => {
    if (trailingIconDetail?.onClick) trailingIconDetail.onClick(value);
  };

  return (
    <div className="flex w-full flex-col gap-1">
      <div
        className={`${className || ''} ${
          sizeClass[inputSize]
        } flex w-full flex-row items-center self-stretch ${name === 'company' ? 'group-[.company-found]:rounded-b-none group-[.company-found]:border-purple-200' : ''} ${enterprise ? 'rounded-md border' : id?.includes('startDatePicker') ? 'rounded-l-sm border-2' : id?.includes('endDatePicker') ? 'rounded-r-sm border-2' : 'rounded-sm border-2'} border-input-stroke px-3 py-1 transition-all duration-200 focus:ring-0 ${
          disabled || loading
            ? 'border-input-disabled! bg-input-bg-disabled'
            : `bg-input-bg ${inputFocus ? 'border-primary! border-2!' : ''}`
        }${` `} ${hidden ? 'hidden' : ''}${` `} ${gap} ${validation ? 'border-red-600!' : ''}`}
      >
        {leading && (
          <i
            className={`${leading} flex! h-6 w-6! items-center justify-center`}
          />
        )}
        {!!prefilledField && (
          <span
            className={`font-semibold whitespace-nowrap ${
              prefilledField?.className || ''
            } ${
              disabled
                ? 'text-input-disabled'
                : prefilledField?.disabled
                  ? 'text-gray-300'
                  : 'text-gray-900'
            } `}
          >
            {prefilledField.text}
          </span>
        )}
        {inputPrefix}
        <input
          data-test-id={testId}
          onMouseDown={(e) => {
            e.stopPropagation();
            if (onMouseDown) onMouseDown(e);
          }}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          disabled={disabled}
          className={inputClass}
          type={type}
          name={name}
          placeholder={placeholder}
          onChange={onChange}
          onClick={handleClick}
          onFocus={handleOnFocus}
          onBlur={(e) => {
            setInputFocus(false);
            if (onBlur) onBlur(e);
          }}
          id={id}
          onKeyDown={onKeyDown}
          value={value}
          accept={accept}
          readOnly={readOnly}
          ref={ref}
          required={required}
          minLength={minLength}
          maxLength={maxLength}
        />
        {enableCopyToClipboard && (
          <CopyToClipboard
            textToCopy={String(value)}
            tooltipText="Copy"
            tooltipDirection="top"
            duotoneIcon
          />
        )}
        {loading && <Loading size="small" color="#4D70EB" type="bold" />}
        {inputSuffix}
        {(trailingIcon || trailingIconDetail) && !loading && (
          <Icon
            className="gap-[-1.5rem] p-0"
            id={trailingIconDetail?.id || id + 'icon'}
            icon={trailingIconDetail?.icon || trailingIcon}
            size={trailingIconDetail?.size || 'medium'}
            type={trailingIconDetail?.type || 'static'}
            onClick={trailingIconClick}
            status={trailingIconDetail?.status || 'normal'}
            tooltip={trailingIconDetail?.tooltip || ''}
            tooltipDirection={trailingIconDetail?.tooltipDirection}
            tooltipPortal={trailingIconDetail?.tooltipPortal}
          />
        )}
      </div>
      {!validation && helpText && (
        <div className="text-xs leading-4 font-normal text-gray-500">
          {helpText}
        </div>
      )}
      {validation === true && (
        <div className="text-xs leading-4 font-normal text-red-600">
          {validationMessage || 'This field is required'}
        </div>
      )}
    </div>
  );
};
